import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProfileDropdown from './ProfileDropdown';
import NotificationSettingsModal from './NotificationSettingsModal';
import ProfileModal from './ProfileModal';
import { HelpDropdown } from '../Help';

// SVG Home Icon - simple house outline
const HomeIcon = () => (
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

// SVG Print Icon - simple printer outline
const PrintIcon = () => (
  <svg 
    width="18" 
    height="18" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{ display: 'block' }}
  >
    <polyline points="6 9 6 2 18 2 18 9" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <rect x="6" y="14" width="12" height="8" />
  </svg>
);

// Standard button size for all header buttons
const BUTTON_SIZE = '44px';

// Header button with consistent hover styling - red only on hover
const HeaderButton = ({ onClick, children, title, dataTutorial }) => {
  return (
    <button
      onClick={onClick}
      data-tutorial={dataTutorial}
      title={title}
      style={{
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        padding: 0,
        background: 'transparent',
        borderRadius: '8px',
        cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        transition: 'all 0.2s ease',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.9rem',
        fontWeight: '500',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
        e.currentTarget.style.borderColor = '#dc3545';
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {children}
    </button>
  );
};

const HeaderButtons = ({ 
  user,
  permissions,
  location, 
  showAdminPanel, 
  setShowAdminPanel, 
  handleLogout,
  onCreateProject,
  onHelpClick,
  onLearningClick,
  onContactClick,
  onFAQClick
}) => {
  const navigate = useNavigate();
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  
  // Use centralized permission checks
  const isPM = permissions?.isPM;
  
  // Check if we are on the projects list page (used for New Project button visibility)
  const isOnProjectsList = location.pathname === '/projects';

  return (
    <div data-tutorial="header-buttons" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      
      {/* 1. Home Button - SVG icon */}
      <HeaderButton
        onClick={() => {
          navigate('/');
          setShowAdminPanel(false);
        }}
        dataTutorial="home-button"
        title="Home"
      >
        <HomeIcon />
      </HeaderButton>

      {/* Create Project button - only for PM users (not a square button) */}
      {isPM && isOnProjectsList && onCreateProject && (
        <button
          onClick={onCreateProject}
          data-tutorial="create-project"
          title="Create New Project"
          style={{
            padding: '0.5rem 1rem',
            background: 'transparent',
            borderRadius: '8px',
            cursor: 'pointer',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.2s ease',
            color: 'white',
            fontSize: '0.9rem',
            fontWeight: '500',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
            e.currentTarget.style.borderColor = '#dc3545';
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          + New
        </button>
      )}

      {/* Help Dropdown */}
      <HelpDropdown
        onHelpClick={onHelpClick}
        onLearningClick={onLearningClick}
        onContactClick={onContactClick}
        onFAQClick={onFAQClick}
      />
      
      {/* Print Button */}
      <HeaderButton
        onClick={() => window.print()}
        title="Print this page"
      >
        <PrintIcon />
      </HeaderButton>
      
      {/* Profile Dropdown */}
      <ProfileDropdown
        user={user}
        onLogout={handleLogout}
        onEditProfile={() => setShowProfile(true)}
        onNotificationSettings={() => setShowNotificationSettings(true)}
      />

      <NotificationSettingsModal
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
      />

      <ProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        user={user}
      />
    </div>
  );
};

export default HeaderButtons;
